import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { I18nService } from '@recruitee/i18n';

import { IconName } from '../../icons-container/icons.type';
import { ComponentVariant, Variants } from '../variant/variant';

type Rating = { value: number; label: string };

@Component({
  selector: 'rt-stars-rating',
  templateUrl: './stars-rating.component.html',
  styleUrls: ['./stars-rating.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.no-rating]': '!rating',
    '[class]': 'variant',
  },
})
export class StarsRatingComponent implements OnChanges {
  public static readonly VARIANTS: Variants = ['ltr', ['ltr', 'rtl']];
  @Input() @ComponentVariant(StarsRatingComponent.VARIANTS) public variant: string;
  @Input() public rating: number;
  @Input() public userRating: number;
  @Input() public disabled: boolean = false;
  @Input() public hideRating: boolean = false;

  @Output() public ratingSelect: EventEmitter<number> = new EventEmitter();

  public label: null | string;
  public ratings: Rating[];
  public rated: boolean = false;
  public roundedRating: string;

  private value: number;
  private hover: boolean = false;

  constructor(private cdRef: ChangeDetectorRef, private i18n: I18nService) {
    this.ratings = [
      { value: 1, label: this.i18n.translate('rtu.common.rating.poor') },
      { value: 2, label: this.i18n.translate('rtu.common.rating.potential') },
      { value: 3, label: this.i18n.translate('rtu.common.rating.good') },
      { value: 4, label: this.i18n.translate('rtu.common.rating.very_good') },
      { value: 5, label: this.i18n.translate('rtu.common.rating.excellent') },
    ];
  }

  public ngOnInit(): void {
    this.resetRating();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rating) {
      this.resetRating();
      this.roundedRating = this.getRoundedRating();
    }
  }

  public resetRating(): void {
    this.label = null;
    this.hover = false;
    if (!this.rated) {
      this.value = this.rating || 0;
    }
  }

  public showLabel(): boolean {
    return this.hover && !this.rated;
  }

  public showRating(): boolean {
    return Boolean(!this.hover && !this.rated && this.rating && this.rating > 0);
  }

  // @ts-ignore
  public iconName(rating: Rating): IconName {
    if (this.value <= rating.value - 1) return 'star-border';
    else if (this.value > rating.value - 1 && this.value < rating.value) return 'star-half';
    else if (this.value >= rating.value) return 'star';
  }

  public getRoundedRating(): string {
    if (this.rating) {
      return this.rating.toFixed(1);
    }
    return '';
  }

  public onRatingHover(rating: Rating): void {
    this.hover = true;
    if (!this.rated) {
      this.label = rating.label;
      this.value = rating.value;
      this.cdRef.detectChanges();
    }
  }

  public trackByValue(index: number, rating: Rating): number {
    return rating.value;
  }

  public onRatingBlur(): void {
    this.resetRating();
    this.cdRef.detectChanges();
  }

  public onRatingClick(rating: Rating): void {
    this.ratingSelect.emit(rating.value);
    this.value = rating.value;
    this.rated = true;
    setTimeout(() => {
      this.rated = false;
      this.value = this.rating || 0;
      this.cdRef.detectChanges();
    }, 2000);
  }
}
